import React from 'react';

import * as ROUTES from '@constants/routes';

import {
  useAdminAddPolicyToOperation,
  useAdminListPolicies,
} from 'generated/admin.generated';
import type { OperationModel } from 'generated/types';

import { Editor } from '../Common';

const messages: Editor.EditorMessages = {
  add: { id: 'admin.operations.policies.add' },
  remove: { id: 'admin.operations.policies.remove' },
  confirmAdd: {
    id: 'admin.operations.policies.add.confirm',
  },
  confirmRemove: {
    id: 'admin.operations.policies.remove.confirm',
  },
  title: { id: 'admin.operations.associated.policies' },
};

interface PolicyEditorProps {
  /** The operation to which the policy will be attached. If undefined, the onPolicySelect callback will be called */
  operation?: OperationModel;
  loading: boolean;
  onPolicySelect?: (option: Editor.EditorOption) => void;
}

const OperationPolicyEditor = ({
  operation,
  loading: baseLoading,
  onPolicySelect,
}: PolicyEditorProps) => {
  const { data: policiesData, loading: policiesLoading } =
    useAdminListPolicies();
  const [addOperationPolicy] = useAdminAddPolicyToOperation();

  const handleAddPolicy = async (option: Editor.EditorOption) => {
    if (!operation) {
      onPolicySelect?.(option);
      return;
    }
    await addOperationPolicy({
      variables: {
        input: {
          policy_id: option.id,
          operation: operation.operation,
        },
      },
    });
  };

  const loading = baseLoading || policiesLoading;

  const options = React.useMemo(() => {
    return policiesData?.listPolicies.map<Editor.EditorOption>((policy) => ({
      id: policy.policy_id,
      name: policy.policy_name ?? '',
      url: `${ROUTES.ADMIN_MANAGE_POLICIES}${policy.policy_id}`,
    }));
  }, [policiesData]);

  const selected = React.useMemo(() => {
    return operation?.policies?.map<Editor.EditorOption>((policy) => ({
      id: policy.policy_id,
      name: policy.policy_name ?? '',
      url: `${ROUTES.ADMIN_MANAGE_POLICIES}${policy.policy_id}`,
    }));
  }, [operation]);

  return (
    <Editor.default
      isCreating={!operation}
      selected={selected}
      options={options}
      messages={messages}
      onAdd={handleAddPolicy}
      loading={loading}
    />
  );
};

export default OperationPolicyEditor;
